import * as React from 'react';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';
import {AuthFlow} from 'src/components/authentication/auth-flow';
import {useDispatch, useSelector} from 'react-redux';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {setTotpOrSmsAuthForTotpVerification} from 'src/action-creators/authentication';
import {
  selectUserEmail,
  selectUserLoginId,
  selectUserPhoneNumber,
  selectChannelSelectionTotpError,
} from 'src/selectors/authentication';

import {
  TitleMedium,
  SubTitleMedium,
  BodyMedium,
  BodyLargeBold,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {
  RadioGroup,
  RadioButton,
} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import classify from 'src/utils/classify';
import css from './authenticator-and-sms-login.css';


type Props = {
  goToPage: (page: string, newMfaFlow?: string | null | undefined) => void;
};

const AuthenticatorAndSmsLogin = ({goToPage}: Props) => {
  const {makeKey, t} = useLabelTranslation();
  const {isExtension} = useEnvironmentContext();
  const [authType, setAuthType] = React.useState('TOTP');
  const [disabled, setDisabled] = React.useState(false);
  const userEmail = useSelector(selectUserEmail);
  const loginId = useSelector(selectUserLoginId);
  const userPhone = useSelector(selectUserPhoneNumber);
  const errorMessage = useSelector(selectChannelSelectionTotpError);
  const dispatch = useDispatch();
  const hashedPhone = userPhone ? ' ******' + userPhone?.slice(-4) : '';
  const smsOtpPageSubtext = [
    t(
      'Enter_the_verification_code_sent_to_your_phone',
      'Enter the verification code sent to your phone',
    ),
    hashedPhone,
  ].join(' ');

  const handleContinueClick = async () => {
    setDisabled(true);
    await dispatch(
      setTotpOrSmsAuthForTotpVerification(userEmail, loginId, authType),
    );
    if (!errorMessage) {
      if (authType === 'SMS') {
        goToPage(AuthFlow.SMS.OTP_INPUT, 'SMS');
      } else {
        goToPage(AuthFlow.TOTP.TOTP_VERIFY, 'TOTP');
      }
    }
    setDisabled(false);
  };
  return (
    <div className={classify(css.container, {[css.containerExt]: isExtension})}>
      {isExtension ? (
        <SubTitleMedium className={css.title}>
          {t('Select_authentication_method', 'Select authentication method')}
        </SubTitleMedium>
      ) : (
        <TitleMedium className={css.title}>
          {t('Select_authentication_method', 'Select authentication method')}
        </TitleMedium>
      )}
      <RadioGroup
        align="vertical"
        name="auth-type"
        value={authType}
        onChange={(value) => {
          setAuthType(value);
        }}
        classNames={{wrapper: css.radioGroup}}
      >
        <RadioButton
          value="TOTP"
          classNames={{
            wrapper: classify(css.radioButton, {
              [css.radioButtonExt]: isExtension,
              [css.radioButtonSelected]: authType === 'TOTP',
            }),
            label: css.radioLabel,
            radio: classify({[css.radio]: !isExtension}),
          }}
        >
          {isExtension ? (
            <BodyMediumBold>
              {t('Authenticator_App', 'Authenticator App')}
            </BodyMediumBold>
          ) : (
            <>
              <BodyLargeBold className={css.stepText}>
                {t('Authenticator_App', 'Authenticator App')}
              </BodyLargeBold>

              <BodyMedium color="secondary">
                {t(
                  'Enter_the_6digit_code_from_your_authenticator_app',
                  'Enter the 6-digit code from your authenticator app',
                )}
              </BodyMedium>
            </>
          )}
        </RadioButton>
        <RadioButton
          value="SMS"
          classNames={{
            wrapper: classify(css.radioButton, {
              [css.radioButtonExt]: isExtension,
              [css.radioButtonSelected]: authType === 'SMS',
            }),
            label: css.radioLabel,
          }}
        >
          {isExtension ? (
            <BodyMediumBold>{t('Text_Message', 'Text Message')}</BodyMediumBold>
          ) : (
            <>
              <BodyLargeBold className={css.stepText}>
                {t('Text_Message', 'Text Message')}
              </BodyLargeBold>
              <BodyMedium color="secondary">{smsOtpPageSubtext}</BodyMedium>
            </>
          )}
        </RadioButton>
      </RadioGroup>
      <Button
        onClick={handleContinueClick}
        size={isExtension ? 'small' : 'medium'}
        type="primary"
        disabled={disabled}
      >
        {t('Continue', 'Continue')}
      </Button>
      {errorMessage && (
        <InContextAlert semantic="danger">
          {t(makeKey(errorMessage), errorMessage)}
        </InContextAlert>
      )}
    </div>
  );
};

export default AuthenticatorAndSmsLogin;
